// First-available variant resolution.
//
// Shopify resolves `option_values` by position, so choosing a colour keeps the
// current size even when that pair is sold out — the picker lands on an
// unavailable variant and the button reads "Unavailable".
//
// This listens in the capture phase, ahead of the theme's own change handler,
// and corrects the *other* options to the first available combination that
// keeps the value just clicked. The theme then reads the corrected selection
// and requests that variant as normal.

function readMatrix(picker) {
  if (picker._vaMatrix !== undefined) return picker._vaMatrix;
  const el = picker.querySelector('[data-variant-matrix]');
  let matrix = null;
  if (el) {
    try {
      matrix = JSON.parse(el.textContent || 'null');
    } catch (e) {
      matrix = null;
    }
  }
  picker._vaMatrix = matrix;
  return matrix;
}

// One entry per option position, in DOM order.
function optionGroups(picker) {
  const groups = [];
  picker.querySelectorAll('input[type="radio"][data-fieldset-index]').forEach((input) => {
    const i = Number(input.dataset.fieldsetIndex);
    if (Number.isNaN(i)) return;
    (groups[i] ||= []).push(input);
  });
  picker.querySelectorAll('select[data-fieldset-index]').forEach((select) => {
    const i = Number(select.dataset.fieldsetIndex);
    if (Number.isNaN(i)) return;
    groups[i] = select;
  });
  return groups;
}

function currentValues(groups) {
  return groups.map((group) => {
    if (!group) return null;
    if (!Array.isArray(group)) return group.value;
    const checked = group.find((input) => input.checked);
    return checked ? checked.value : null;
  });
}

/**
 * The best variant to move to: available, keeps the clicked value, and differs
 * from the current selection as little as possible.
 */
function bestVariant(matrix, index, value, current) {
  let best = null;
  let bestScore = -1;

  for (const variant of matrix) {
    if (!variant.a) continue;
    if (variant.o[index] !== value) continue;

    let score = 0;
    for (let i = 0; i < variant.o.length; i++) {
      if (i !== index && variant.o[i] === current[i]) score++;
    }

    if (score > bestScore) {
      best = variant;
      bestScore = score;
    }
  }

  return best;
}

function applyValue(group, value) {
  if (!group || value == null) return;

  if (!Array.isArray(group)) {
    for (const option of group.options) {
      const match = option.value === value;
      option.selected = match;
      // The theme reads `option[selected]`, the attribute, not the property.
      if (match) option.setAttribute('selected', '');
      else option.removeAttribute('selected');
    }
    return;
  }

  for (const input of group) {
    const match = input.value === value;
    input.checked = match;
    input.dataset.currentChecked = match ? 'true' : 'false';
  }
}

function onChange(event) {
  const picker = event.currentTarget;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const matrix = readMatrix(picker);
  if (!matrix || matrix.length === 0) return;

  const index = Number(target.dataset.fieldsetIndex);
  if (Number.isNaN(index)) return;

  const value = target instanceof HTMLSelectElement ? target.value : target.value;
  const groups = optionGroups(picker);
  const current = currentValues(groups);
  current[index] = value;

  // Leave a selection that is already purchasable alone.
  const exact = matrix.find((v) => v.o.every((o, i) => o === current[i]));
  if (exact && exact.a) return;

  const target_variant = bestVariant(matrix, index, value, current);
  // Every combination for this value is sold out — leave it, so the shopper
  // still sees the option they chose reported as unavailable.
  if (!target_variant) return;

  groups.forEach((group, i) => {
    if (i === index) return;
    applyValue(group, target_variant.o[i]);
  });
}

function init(picker) {
  if (picker._vaInit) return;
  picker._vaInit = true;
  picker.addEventListener('change', onChange, true);
}

function boot(scope) {
  (scope || document).querySelectorAll('variant-picker').forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', (e) => boot(e.target));
new MutationObserver(() => boot()).observe(document.documentElement, { childList: true, subtree: true });
